import {
  ActionIcon,
  Box,
  Group,
  Menu,
  Paper,
  Text,
  Tooltip,
} from "@mantine/core";
import { BarChart } from "@mantine/charts";
import IconDownload from "@tabler/icons-react/dist/esm/icons/IconDownload.mjs";
import IconFileTypeSvg from "@tabler/icons-react/dist/esm/icons/IconFileTypeSvg.mjs";
import IconFileTypeXls from "@tabler/icons-react/dist/esm/icons/IconFileTypeXls.mjs";
import IconPhoto from "@tabler/icons-react/dist/esm/icons/IconPhoto.mjs";
import IconPrinter from "@tabler/icons-react/dist/esm/icons/IconPrinter.mjs";
import { useRef } from "react";
import dayjs from "dayjs";
import { utils, writeFile } from "xlsx";
import { useUsername } from "../../../redux/features/auth/authSlice";
import {
  useSelectAuditTrailChartData,
  useSelectAuditTrailChartType,
  useSelectAuditTrailGroupByValue,
  useSelectAuditTrailSelectValue,
} from "../../../redux/features/auditTrail/auditTrailSlice";
import classes from "../auditTrail.module.scss";

const ChartTooltip = ({ payload, chartType }) => {
  const point = payload?.[0]?.payload;
  if (!point) return null;
  return (
    <Paper px="sm" py="xs" withBorder shadow="md" radius="sm">
      <Text size="sm">
        {point.tooltipName}: {point[chartType]}
      </Text>
    </Paper>
  );
};

const FILE_BASE = "chart";

const STYLE_PROPS = [
  "fill",
  "fill-opacity",
  "stroke",
  "stroke-opacity",
  "stroke-width",
  "stroke-linecap",
  "stroke-linejoin",
  "stroke-dasharray",
  "font-family",
  "font-size",
  "font-weight",
  "text-anchor",
  "opacity",
];

// Mantine/Recharts paint via CSS variables. Once the SVG is serialized into a
// blob/<img>, those vars no longer resolve and colors fall back to black.
// Walk source + clone in lock-step and copy each computed style onto the clone.
const inlineComputedStyles = (sourceSvg, clonedSvg) => {
  const apply = (src, dst) => {
    const computed = window.getComputedStyle(src);
    for (const prop of STYLE_PROPS) {
      const value = computed.getPropertyValue(prop);
      if (value) dst.setAttribute(prop, value);
    }
  };
  apply(sourceSvg, clonedSvg);
  const srcAll = sourceSvg.querySelectorAll("*");
  const dstAll = clonedSvg.querySelectorAll("*");
  for (let i = 0; i < srcAll.length; i++) {
    if (dstAll[i]) apply(srcAll[i], dstAll[i]);
  }
};

const getChartSvg = (containerEl) => {
  const svg = containerEl?.querySelector("svg");
  if (!svg) return null;
  const clone = svg.cloneNode(true);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
  inlineComputedStyles(svg, clone);
  return { clone, rect: svg.getBoundingClientRect() };
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const serializeSvg = (svg) =>
  '<?xml version="1.0" standalone="no"?>\r\n' +
  new XMLSerializer().serializeToString(svg);

const svgToImage = (svgString) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });

const exportSvg = (containerEl) => {
  const chart = getChartSvg(containerEl);
  if (!chart) return;
  const blob = new Blob([serializeSvg(chart.clone)], {
    type: "image/svg+xml;charset=utf-8",
  });
  downloadBlob(blob, `${FILE_BASE}.svg`);
};

const exportImage = async (containerEl, format) => {
  const chart = getChartSvg(containerEl);
  if (!chart) return;
  const { clone, rect } = chart;
  const scale = 2;
  const canvas = document.createElement("canvas");
  canvas.width = rect.width * scale;
  canvas.height = rect.height * scale;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const img = await svgToImage(serializeSvg(clone));
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  canvas.toBlob(
    (blob) => {
      if (blob) downloadBlob(blob, `${FILE_BASE}.${format}`);
    },
    `image/${format}`,
    format === "jpeg" ? 0.95 : undefined,
  );
};

const printChart = (containerEl) => {
  const chart = getChartSvg(containerEl);
  if (!chart) return;
  const html = `<!doctype html><html><head><title>${FILE_BASE}</title>
    <style>body{margin:0;display:flex;justify-content:center;align-items:center;height:100vh}</style>
    </head><body>${serializeSvg(chart.clone)}</body></html>`;
  const win = window.open("", "_blank");
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
  win.close();
};

const AuditTrailChart = () => {
  const username = useUsername();
  const chartData = useSelectAuditTrailChartData();
  const chartType = useSelectAuditTrailChartType();
  const selectValue = useSelectAuditTrailSelectValue();
  const groupByValue = useSelectAuditTrailGroupByValue();
  const chartRef = useRef(null);

  if (!chartData) return null;

  const data = chartData.map((e, i) => {
    const head = (e.name?.split("|")[0] ?? "").slice(0, 4);
    return {
      ...e,
      name: `${head}.. ${i}`,
      tooltipName: `${e.name} - ${e.groupBy}`,
    };
  });

  const handleExportExcel = () => {
    const employee = `${username} - ${dayjs().format("DD/MM/YYYY")}`;
    const selectTitle =
      "Select criteria: " +
      selectValue.map((e) => `${e.type} - ${e.label}`).join(", ");
    const groupByTitle =
      "Group by: " +
      groupByValue.map((e) => `${e.type} - ${e.label}`).join(", ");

    const rows = chartData.map((e) => [
      `${e.name} - ${e.groupBy}`,
      e.count,
      e.percentage,
    ]);

    const aoa = [
      [employee],
      [selectTitle],
      [groupByTitle],
      [],
      ["", "Count", "Percentage"],
      ...rows,
    ];

    const ws = utils.aoa_to_sheet(aoa);
    const nameWidth = aoa.reduce(
      (max, row) => Math.max(max, String(row[0] ?? "").length),
      20,
    );
    ws["!cols"] = [{ wch: nameWidth }, { wch: 10 }, { wch: 12 }];

    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Sheet1");
    writeFile(wb, `${FILE_BASE}.xlsx`);
  };

  return (
    <Box className={classes.section}>
      <Group justify="space-between" align="center" mb="sm">
        <Text size="sm" fw={600} tt="uppercase" c="secondary.9" lts={1}>
          Results
        </Text>
        <Menu shadow="md" position="bottom-end" withinPortal>
          <Menu.Target>
            <Tooltip label="Export">
              <ActionIcon variant="outline" color="primary.7">
                <IconDownload size={18} />
              </ActionIcon>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Export</Menu.Label>
            <Menu.Item
              leftSection={<IconFileTypeXls size={16} />}
              onClick={handleExportExcel}
            >
              Excel (.xlsx)
            </Menu.Item>
            <Menu.Item
              leftSection={<IconPhoto size={16} />}
              onClick={() => exportImage(chartRef.current, "png")}
            >
              PNG
            </Menu.Item>
            <Menu.Item
              leftSection={<IconPhoto size={16} />}
              onClick={() => exportImage(chartRef.current, "jpeg")}
            >
              JPEG
            </Menu.Item>
            <Menu.Item
              leftSection={<IconFileTypeSvg size={16} />}
              onClick={() => exportSvg(chartRef.current)}
            >
              SVG
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              leftSection={<IconPrinter size={16} />}
              onClick={() => printChart(chartRef.current)}
            >
              Print
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
      <Box ref={chartRef}>
        <BarChart
          h={400}
          data={data}
          dataKey="name"
          series={[{ name: chartType, color: "primary.7" }]}
          tooltipProps={{
            content: ({ payload }) => (
              <ChartTooltip payload={payload} chartType={chartType} />
            ),
          }}
          withLegend={false}
        />
      </Box>
    </Box>
  );
};

export default AuditTrailChart;
